import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersApi } from '../../lib/api/customers';
import { Customer } from './types';
import { ArrowUpDown, User as UserIcon } from 'lucide-react';

type SortField = 'date' | 'alphabet' | 'subscription' | 'status' | 'amount';

export const CustomersPage: React.FC = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const data = await customersApi.getAll();
            setCustomers(data);
        } catch (error) {
            console.error('Failed to load customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc'); // Default to asc
            if (field === 'date') setSortDirection('desc'); // Date default desc
        }
    };

    const sortedCustomers = [...customers].sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
            case 'date':
                comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                break;
            case 'alphabet':
                // Check if name is defined; otherwise fallback to email or empty string
                const nameA = (a.name || a.email || '').toLowerCase();
                const nameB = (b.name || b.email || '').toLowerCase();
                comparison = nameA.localeCompare(nameB);
                break;
            case 'subscription':
                const subA = a.subscription?.plan?.name || 'Free';
                const subB = b.subscription?.plan?.name || 'Free';
                comparison = subA.localeCompare(subB);
                break;
            case 'status':
                const statusA = a.subscription?.status || 'none';
                const statusB = b.subscription?.status || 'none';
                comparison = statusA.localeCompare(statusB);
                break;
            case 'amount':
                const amountA = a.subscription?.plan?.prices?.[0]?.amount || 0;
                const amountB = b.subscription?.plan?.prices?.[0]?.amount || 0;
                comparison = amountA - amountB;
                break;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
    });

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'active': return 'bg-green-50 text-green-700 border-green-100';
            case 'past_due': return 'bg-red-50 text-red-700 border-red-100';
            case 'canceled': return 'bg-neutral-100 text-neutral-600 border-neutral-200';
            case 'trialing': return 'bg-blue-50 text-blue-700 border-blue-100';
            default: return 'bg-neutral-50 text-neutral-500 border-neutral-100';
        }
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">
                {/* Header for sorting */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    <div className="col-span-4 flex items-center cursor-pointer hover:text-neutral-700 select-none" onClick={() => handleSort('alphabet')}>
                        <span className="mr-2">Customer</span>
                        {sortField === 'alphabet' && <ArrowUpDown size={12} />}
                    </div>
                    <div className="col-span-2 flex items-center cursor-pointer hover:text-neutral-700 select-none" onClick={() => handleSort('status')}>
                        <span className="mr-2">Status</span>
                        {sortField === 'status' && <ArrowUpDown size={12} />}
                    </div>
                    <div className="col-span-2 flex items-center cursor-pointer hover:text-neutral-700 select-none" onClick={() => handleSort('subscription')}>
                        <span className="mr-2">Plan</span>
                        {sortField === 'subscription' && <ArrowUpDown size={12} />}
                    </div>
                    <div className="col-span-2 flex items-center cursor-pointer hover:text-neutral-700 select-none" onClick={() => handleSort('amount')}>
                        <span className="mr-2">Amount</span>
                        {sortField === 'amount' && <ArrowUpDown size={12} />}
                    </div>
                    <div className="col-span-2 flex items-center cursor-pointer hover:text-neutral-700 select-none" onClick={() => handleSort('date')}>
                        <span className="mr-2">Joined</span>
                        {sortField === 'date' && <ArrowUpDown size={12} />}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 text-center text-neutral-500 text-sm">Loading customers...</div>
                    ) : sortedCustomers.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <UserIcon size={32} className="mb-3 text-neutral-300" />
                            <p className="text-sm">No customers found</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {sortedCustomers.map(customer => {
                                const price = customer.subscription?.plan?.prices?.[0];
                                return (
                                    <div
                                        key={customer.id}
                                        onClick={() => navigate(`/customers/${customer.id}`)}
                                        className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-neutral-50/80 cursor-pointer transition-colors text-sm group"
                                    >
                                        <div className="col-span-4 min-w-0">
                                            <div className="font-medium text-neutral-900 truncate">{customer.name || 'Unnamed Customer'}</div>
                                            <div className="text-neutral-500 truncate text-xs">{customer.email}</div>
                                        </div>
                                        <div className="col-span-2">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium border uppercase ${getStatusColor(customer.subscription?.status)}`}>
                                                {customer.subscription?.status || 'No Plan'}
                                            </span>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="text-sm text-neutral-700">
                                                {customer.subscription?.plan?.name || '-'}
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            {price ? (
                                                <div className="text-sm text-neutral-900">
                                                    {formatCurrency(price.amount, price.currency)}
                                                    <span className="text-neutral-400 text-xs ml-1">/{price.interval}</span>
                                                </div>
                                            ) : (
                                                <div className="text-neutral-400 text-xs">-</div>
                                            )}
                                        </div>
                                        <div className="col-span-2 text-xs text-neutral-500">
                                            {new Date(customer.createdAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
